import {
  AUMSourceEnum,
  PublicReturnFeeEnum,
  PublicReturnGeneralClassificationEnum,
  PublicReturnSourceEnum,
  StreamPeriodicityEnum,
  StreamSourceEnum,
} from '../../enums/periodicStream.enum';
import {
  StoreModel,
  StoreModelDate,
  StoreModelType,
  UTILS,
} from '@aksia/infrastructure';
import { TPeriodicStream } from '../../types/periodic-stream.type';
import { CurrencyStringEnum, EntityTypeEnum } from '../../enums/entity.enum';
import {
  IAUMStreamPoint,
  IPeriodicStream,
  IPeriodicStreamPoint,
  IPublicReturnStreamPoint,
} from '../../interfaces/periodic-stream/periodic-stream.interface';
import { PERIODIC_STREAMS } from '../../constants';

class PeriodicStreamPointStoreModel extends StoreModel {
  $disabled?: boolean = false;
}

export class PeriodicStreamPoint
  extends PeriodicStreamPointStoreModel
  implements IPeriodicStreamPoint
{
  id?: number;

  @StoreModelType(() => StoreModelDate)
  asOf?: Date = undefined;
  source?: StreamSourceEnum | AUMSourceEnum | PublicReturnSourceEnum =
    undefined;
  value?: number = undefined;

  override $onTransform = () => {
    this.asOf = this.asOf ? new StoreModelDate(this.asOf) : undefined;
  };
}

export class PublicReturnStreamPoint
  extends PeriodicStreamPoint
  implements IPublicReturnStreamPoint
{
  override source?: PublicReturnSourceEnum = undefined;
  fundId?: number = undefined;
  feeType?: PublicReturnFeeEnum = undefined;
  classification?: PublicReturnGeneralClassificationEnum = undefined;

  @StoreModelType(() => StoreModelDate)
  estimationAsOf?: Date = undefined;

  markedForDeletion?: boolean = false;
}

export class AUMStreamPoint
  extends PeriodicStreamPoint
  implements IAUMStreamPoint
{
  override source?: AUMSourceEnum = undefined;
  currency?: CurrencyStringEnum = undefined;
  relation?: number = undefined;
  entityIds?: string = undefined;
  isDeleted?: boolean = false;
}

export class PeriodicStream<T = PeriodicStreamPoint>
  extends StoreModel
  implements IPeriodicStream<T>
{
  entityId?: number;
  entityTypeId?: EntityTypeEnum;
  streamType?: TPeriodicStream;
  periodicity?: StreamPeriodicityEnum;

  @StoreModelType(() => PeriodicStreamPoint)
  datapoints?: Array<T>;
  ytd?: Array<T>;

  historyIsLoaded?: boolean;

  @StoreModelType(() => StoreModelDate)
  selectedAsOf?: Date;

  @StoreModelType(() => StoreModelDate)
  minAsOf?: Date;

  @StoreModelType(() => StoreModelDate)
  maxAsOf?: Date;
  selectedSource?: StreamSourceEnum | PublicReturnSourceEnum | AUMSourceEnum;

  //#region AUM Specific

  selectedCurrency?: CurrencyStringEnum;

  //#endregion

  //#region Public Returns Specific

  selectedFeeType?: PublicReturnFeeEnum;
  selectedClassification?: PublicReturnGeneralClassificationEnum;

  @StoreModelType(() => StoreModelDate)
  selectedEstimationAsOf?: Date;

  //#endregion

  selectedDataPoint?: T;
  markedDataPoints?: Array<T>;
  selectedDataPointErrors?: Array<string>;

  static getStreamPointHeader(streamType: TPeriodicStream) {
    switch (streamType) {
      case PERIODIC_STREAMS.COMPANY_AUM:
        return 'Company AUM';
      case PERIODIC_STREAMS.PROGRAM_AUM:
        return 'Program AUM';
      case PERIODIC_STREAMS.FUND_AUM:
        return 'Fund AUM';
      case PERIODIC_STREAMS.PUBLIC_RETURNS:
        return 'Public Returns';
      case PERIODIC_STREAMS.GROSSIRR:
        return 'Gross IRR';
      case PERIODIC_STREAMS.NETIRR:
        return 'Net IRR';
      case PERIODIC_STREAMS.GROSSMOIC:
        return 'Gross TVPI';
      case PERIODIC_STREAMS.NETMOIC:
        return 'Net TVPI';
      case PERIODIC_STREAMS.NETDPI:
        return 'Net DPI';
      case PERIODIC_STREAMS.NETRVPI:
        return 'Net RVPI';
      case PERIODIC_STREAMS.INVESTED_CAPITAL:
        return 'Invested Capital';
      case PERIODIC_STREAMS.GROSSINCOMERETURN:
        return 'Gross Income QTD Return';
      case PERIODIC_STREAMS.GROSSAPPRECIATIONRETURN:
        return 'Gross Appreciation QTD Return';
      case PERIODIC_STREAMS.TOTALGROSSRETURN:
        return 'Total Gross QTD Return';
      case PERIODIC_STREAMS.NETINCOMERETURN:
        return 'Net Income QTD Return';
      case PERIODIC_STREAMS.NETAPPRECIATIONRETURN:
        return 'Net Appreciation QTD Return';
      case PERIODIC_STREAMS.TOTALNETRETURN:
        return 'Total Net QTD Return';
      default:
        return UTILS.STRING.multiReplace(
          streamType?.charAt(0)?.toUpperCase() + streamType.slice(1),
          UTILS.CONSTANTS.DEFAULT_ENUM_REPLACE_RULES,
        );
    }
  }

  static getStreamPointSuffix(streamType: TPeriodicStream) {
    switch (streamType) {
      case PERIODIC_STREAMS.PUBLIC_RETURNS:
      case PERIODIC_STREAMS.GROSSIRR:
      case PERIODIC_STREAMS.NETIRR:
      case PERIODIC_STREAMS.GROSSINCOMERETURN:
      case PERIODIC_STREAMS.GROSSAPPRECIATIONRETURN:
      case PERIODIC_STREAMS.TOTALGROSSRETURN:
      case PERIODIC_STREAMS.NETINCOMERETURN:
      case PERIODIC_STREAMS.NETAPPRECIATIONRETURN:
      case PERIODIC_STREAMS.TOTALNETRETURN:
        return ' %';
      case PERIODIC_STREAMS.GROSSMOIC:
      case PERIODIC_STREAMS.NETMOIC:
      case PERIODIC_STREAMS.NETDPI:
      case PERIODIC_STREAMS.NETRVPI:
      case PERIODIC_STREAMS.SCALEUPRATIO:
        return ' x';
      default:
        return '';
    }
  }

  static getStreamPointFooter(
    asOf: Date | undefined,
    source:
      StreamSourceEnum | AUMSourceEnum | PublicReturnSourceEnum | undefined,
    sourceType:
      | typeof StreamSourceEnum
      | typeof AUMSourceEnum
      | typeof PublicReturnSourceEnum,
  ) {
    const asOfDesc = UTILS.FORMAT.toDateFormat(asOf, 'MMM-yyyy');
    const sourceDesc = source
      ? sourceType.toOptions()?.find((option) => option.value === source)?.label
      : undefined;
    return `${asOfDesc ?? ''}${asOfDesc && sourceDesc ? ',' : ''} ${sourceDesc ?? ''}`;
  }

  static getStreamClass(streamType: TPeriodicStream) {
    switch (streamType) {
      case PERIODIC_STREAMS.FUND_AUM:
      case PERIODIC_STREAMS.PROGRAM_AUM:
      case PERIODIC_STREAMS.COMPANY_AUM:
        return AUMPeriodicStream;
      case PERIODIC_STREAMS.PUBLIC_RETURNS:
        return PublicReturnPeriodicStream;
      default:
        return PeriodicStream;
    }
  }

  static getStreamPointClass(streamType: TPeriodicStream) {
    switch (streamType) {
      case PERIODIC_STREAMS.FUND_AUM:
      case PERIODIC_STREAMS.PROGRAM_AUM:
      case PERIODIC_STREAMS.COMPANY_AUM:
        return AUMStreamPoint;
      case PERIODIC_STREAMS.PUBLIC_RETURNS:
        return PublicReturnStreamPoint;
      default:
        return PeriodicStreamPoint;
    }
  }

  static isTheSameAsOf(a: Date | undefined, b: Date | undefined) {
    return (
      a !== undefined &&
      b !== undefined &&
      a?.getFullYear() === b?.getFullYear() &&
      a?.getMonth() === b?.getMonth()
    );
  }

  static getStreamYears(stream: PeriodicStream) {
    const currentYear = new Date().getFullYear();
    const minYear = UTILS.DATE.isDate(stream.minAsOf)
      ? (stream.minAsOf?.getFullYear() ?? currentYear)
      : currentYear;
    return Array.from(Array(currentYear - minYear + 1).keys()).map((i) =>
      (currentYear - i).toString(),
    );
  }

  static conditionalFormatting(dataItem: unknown) {
    const point = dataItem as PublicReturnStreamPoint;
    if (!point || UTILS.OBJECT.isEmpty(point?.value)) return '';

    let css = '';
    switch (point.classification) {
      case PublicReturnGeneralClassificationEnum.Master:
      case PublicReturnGeneralClassificationEnum.InvestmentProgram:
        css = 'bg-orange-200';
        break;
      case PublicReturnGeneralClassificationEnum.Backtested:
      case PublicReturnGeneralClassificationEnum.CarveOut:
      case PublicReturnGeneralClassificationEnum.Composite:
      case PublicReturnGeneralClassificationEnum.FundOfOne:
      case PublicReturnGeneralClassificationEnum.InternalOnly:
        css = 'bg-orange-400';
        break;
    }

    if (point.estimationAsOf) {
      css += ' opacity-50';
    }

    if (!point || UTILS.OBJECT.isEmpty(point?.value)) return css;
    else
      return point.value! < 0 ? css + ' text-red-600' : css + ' text-green-600';
  }
}

export class AUMPeriodicStream extends PeriodicStream<AUMStreamPoint> {
  @StoreModelType(() => AUMStreamPoint, 'AUMPeriodicStream')
  override datapoints?: Array<AUMStreamPoint> = undefined;
}

export class PublicReturnPeriodicStream extends PeriodicStream<PublicReturnStreamPoint> {
  @StoreModelType(() => PublicReturnStreamPoint, 'PublicReturnPeriodicStream')
  override datapoints?: Array<PublicReturnStreamPoint> = undefined;
}
